import { Directive, Input, TemplateRef, ViewContainerRef } from "@angular/core";
import { merge, of } from "rxjs";
import { take } from "rxjs/operators";
import { LayoutService } from "./layout.service";
import { LayoutComponentComponent } from "./layout-component/layout-component.component";
import { LayoutManagerComponent } from "./layout-manager/layout-manager.component";

@Directive({
    selector: "[adaptLayoutComponentEnabled]",
    standalone: false,
})
export class LayoutComponentEnabledDirective<T = unknown> {
    public static ngTemplateGuard_adaptLayoutComponentEnabled: "binding";

    private hasView = false;

    constructor(
        private templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef,
        private layoutManager: LayoutManagerComponent,
        private layoutComponent: LayoutComponentComponent,
    ) {
    }

    @Input()
    public set adaptLayoutComponentEnabled(condition: T) {
        this.setIsEnabled(!!condition);

        if (condition && !this.hasView) {
            // pass thru input to context so you can do "(async$) as something", {{something}}
            this.viewContainer.createEmbeddedView(this.templateRef, {
                $implicit: condition,
                adaptLayoutComponentEnabled: condition,
            });
            this.hasView = true;
        } else if (!condition && this.hasView) {
            this.viewContainer.clear();
            this.hasView = false;
        }
    }

    public setIsEnabledFromArray(data: unknown[]) {
        this.setIsEnabled(data.length > 0);
    }

    public setIsEnabled(isEnabled: boolean) {
        this.layoutComponent.enabled = isEnabled;

        if (isEnabled) {
            const { column: columnComponent } = LayoutService.findComponentInLayout(this.layoutManager.layout.columns, this.layoutComponent.layoutEntity.id);

            // make sure columns have been enabled/disabled before we check if we need to move the component
            // include component update in this in case update doesn't cause column update.
            merge(
                // having troubles directly injecting layoutColumn when the component is used in an ng-template and projected into the column
                // so just get the column ref from the layout manager
                columnComponent?.ref?.updated$?.asObservable() ?? of(void 0),
                this.layoutComponent.updated$.asObservable(),
            ).pipe(
                take(1),
            ).subscribe(() => {
                const desiredLayout = this.layoutManager.cloneLayout(true);
                const {
                    component,
                    column,
                } = LayoutService.findComponentInLayout(desiredLayout.columns, this.layoutComponent.layoutEntity.id);

                // column is not enabled, we should move it to an enabled column instead
                if (column && (!column.options.enabled && !column.options.manuallyEnabled)) {
                    const enabledColumns = desiredLayout.columns.filter((col) => col.options.enabled);
                    const destColumn = enabledColumns[enabledColumns.length - 1];

                    // move within cloned layout, then reconcile
                    if (component && column) {
                        this.layoutManager.moveComponent(component, column, destColumn, 0);
                        this.layoutManager.reconcileAndUpdateLayout(desiredLayout).subscribe();
                    }
                }
            });
        }
    }
}
